/**
 * Buy Now Catalog Image Rotation
 * Handles automatic image rotation for product displays
 */

class BuyNowCatalog {
  constructor() {
    this.rotationIntervals = new Map();
    this.observers = new Map();
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupRotation());
    } else {
      this.setupRotation();
    }
  }

  setupRotation() {
    const containers = document.querySelectorAll('.rotating-image-container');

    containers.forEach(container => {
      this.setupContainerRotation(container);
    });

    // Setup intersection observer for performance
    this.setupIntersectionObserver();
  }

  setupContainerRotation(container) {
    const images = container.querySelectorAll('.rotating-image');

    // Only setup rotation if there are multiple images
    if (images.length < 2) return;

    const rotationSpeed = parseInt(container.dataset.rotationSpeed) || 3000;
    let currentIndex = 0;

    // Ensure first image is active
    images.forEach((img, index) => {
      img.classList.toggle('active', index === 0);
    });

    const rotateImages = () => {
      // Remove active class from current image
      images[currentIndex].classList.remove('active');

      // Move to next image
      currentIndex = (currentIndex + 1) % images.length;

      // Add active class to new image
      images[currentIndex].classList.add('active');
    };

    // Store rotation function for later control
    container.rotateImages = rotateImages;
    container.rotationSpeed = rotationSpeed;
    container.images = images;
    container.currentIndex = currentIndex;
  }

  setupIntersectionObserver() {
    // Use Intersection Observer to only animate visible elements
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const container = entry.target;

        if (entry.isIntersecting) {
          this.startRotation(container);
        } else {
          this.stopRotation(container);
        }
      });
    }, observerOptions);

    // Observe all rotation containers
    document.querySelectorAll('.rotating-image-container').forEach(container => {
      if (container.images && container.images.length > 1) {
        observer.observe(container);
      }
    });

    this.intersectionObserver = observer;
  }

  startRotation(container) {
    // Don't start if already rotating or no rotation function
    if (this.rotationIntervals.has(container) || !container.rotateImages) {
      return;
    }

    const intervalId = setInterval(() => {
      container.rotateImages();
    }, container.rotationSpeed);

    this.rotationIntervals.set(container, intervalId);
  }

  stopRotation(container) {
    const intervalId = this.rotationIntervals.get(container);

    if (intervalId) {
      clearInterval(intervalId);
      this.rotationIntervals.delete(container);
    }
  }

  // Manual controls for hover effects (optional)
  pauseRotation(container) {
    this.stopRotation(container);
    container.dataset.paused = 'true';
  }

  resumeRotation(container) {
    if (container.dataset.paused === 'true') {
      container.dataset.paused = 'false';
      this.startRotation(container);
    }
  }

  // Cleanup method
  destroy() {
    // Clear all intervals
    this.rotationIntervals.forEach(intervalId => {
      clearInterval(intervalId);
    });
    this.rotationIntervals.clear();

    // Disconnect intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
}

// Initialize when script loads
let buyNowCatalogInstance;

// Handle theme editor updates
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('buy-now-catalog')) {
    // Reinitialize for dynamically loaded sections
    if (buyNowCatalogInstance) {
      buyNowCatalogInstance.destroy();
    }
    buyNowCatalogInstance = new BuyNowCatalog();
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId.includes('buy-now-catalog')) {
    if (buyNowCatalogInstance) {
      buyNowCatalogInstance.destroy();
    }
  }
});

// Initial setup
buyNowCatalogInstance = new BuyNowCatalog();

// Optional: Add hover pause functionality
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.rotating-image-container').forEach(container => {
    container.addEventListener('mouseenter', () => {
      if (buyNowCatalogInstance) {
        buyNowCatalogInstance.pauseRotation(container);
      }
    });

    container.addEventListener('mouseleave', () => {
      if (buyNowCatalogInstance) {
        buyNowCatalogInstance.resumeRotation(container);
      }
    });
  });
});